import { Component, computed, forwardRef, inject, input, OnInit, viewChild } from '@angular/core';
import {
  ControlValueAccessor,
  FormControlDirective,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { IFormInput } from '@mwe/models';
import { FormComponent } from '../../form.component';
import { ClickPreventDefaultDirective } from '../../../directives/click/click-prevent-default.directive';
import { NgClass } from '@angular/common';
import ShortUniqueId from 'short-unique-id';
import { ProductIconsComponent } from '../../../product-icons/product-icons.component';
import { TranslatePipe } from '@ngx-translate/core';
import { getAddressPostCodeAndStreetFormatted } from '@mwe/utils';

@Component({
  selector: 'mwe-selectable-card-radio-input',
  templateUrl: './selectable-card-radio-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectableCardRadioInputComponent,
      multi: true,
    },
  ],
  imports: [
    FormsModule,
    forwardRef(() => ClickPreventDefaultDirective),
    ReactiveFormsModule,
    NgClass,
    ProductIconsComponent,
    TranslatePipe,
  ],
})
export class SelectableCardRadioInputComponent implements ControlValueAccessor, OnInit {
  parentForm = input<UntypedFormGroup>();
  inputElem = input<IFormInput>();
  isLoading = input<boolean>(false);
  formControlDirective = viewChild<FormControlDirective>(FormControlDirective);
  postCodeAndStreet = computed(() => getAddressPostCodeAndStreetFormatted(this.inputElem().content.address));
  isValid = computed<boolean>(() => {
    return this.formControlDirective()?.control?.valid;
  });
  control = computed<UntypedFormControl>(() => {
    return this.parentForm().controls[this.inputElem().name] as UntypedFormControl;
  });
  inputID: string = 'selectable-radio-' + new ShortUniqueId({ dictionary: 'alpha' }).rnd(8);
  isRequired: boolean;
  formComponent = inject(
    forwardRef(() => FormComponent),
    { optional: true },
  );

  ngOnInit() {
    this.isRequired = this.inputElem().validators?.indexOf(Validators.required) > -1;
  }

  registerOnTouched(fn: any): void {
    this.formControlDirective().valueAccessor.registerOnTouched(fn);
  }

  registerOnChange(fn: any): void {
    this.formControlDirective().valueAccessor.registerOnChange(fn);
  }

  writeValue(obj: any): void {
    this.formControlDirective().valueAccessor.writeValue(obj);
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDirective().valueAccessor.setDisabledState(isDisabled);
  }

  get ariaDescribedBy(): string | undefined {
    if (this.formComponent) {
      // Error State hat Vorrang für Screenreader
      if (this.formComponent.isInputErrorVisible(this.inputElem().componentType)) {
        return `form-input-error--${this.inputElem.name}`;
      } else if (this.formComponent.isInputHelpInfoVisible(this.inputElem)) {
        return `form-input-info--${this.inputElem.name}`;
      }
    }
    return undefined;
  }
}
